import { useEffect, useRef, useState } from 'preact/hooks'
import type {
  CompteComptable,
  ConfigurationComptable,
  ElementPayant,
  MappingComptable,
  RapportFinancier,
  TypeCompte,
} from '../../serveur/src/comptabilite/types.ts'
import type { SuiviFinancier } from '../../serveur/src/comptabilite/suivi.ts'
import { FinancesDocumentsPane } from './finances-documents.tsx'
import { CommercePane } from './commerce.tsx'

const argent = (cents: number) =>
  new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(
    cents / 100,
  )
const jour = (d: Date) =>
  new Intl.DateTimeFormat('fr-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d)
const suivant = (date: string) =>
  new Date(Date.parse(`${date}T12:00:00Z`) + 86400000)
    .toISOString()
    .slice(0, 10)
const types: Record<TypeCompte, string> = {
  actif: 'Actif',
  passif: 'Passif',
  produit: 'Produit',
  charge: 'Charge',
  capitaux: 'Capitaux propres',
}
const nomsEnv: Record<string, string> = {
  actif: 'Paiements réels',
  test: 'Stripe test',
  simule: 'Démonstration',
  essai: 'Aperçus',
  inconnu: 'Historique à vérifier',
  tous: 'Tous les modes',
}

export function FinancesPane({
  baseUrl,
  permissions,
  onAuth,
  onOuvrirDemande,
}: {
  baseUrl: string
  permissions: string[]
  onAuth?: () => void
  onOuvrirDemande?: (id: string) => void
}) {
  const autorise = (p: string) =>
    permissions.includes('*') || permissions.includes(p)
  const [vue, setVue] = useState<'commerce' | 'rapport' | 'suivi' | 'factures' | 'lots' | 'configuration'>('commerce')
  const [onglet, setOnglet] = useState<'transactions' | 'ecritures'>(
    'transactions',
  )
  const [du, setDu] = useState(jour(new Date()).slice(0, 8) + '01'),
    [fin, setFin] = useState(jour(new Date())),
    [env, setEnv] = useState('actif')
  const [configuration, setConfiguration] =
      useState<ConfigurationComptable | null>(null),
    [rapport, setRapport] = useState<RapportFinancier | null>(null),
    [suivi, setSuivi] = useState<SuiviFinancier | null>(null)
  const [revision, setRevision] = useState(0),
    [charge, setCharge] = useState(false),
    [erreur, setErreur] = useState(''),
    [avis, setAvis] = useState('')
  const [compte, setCompte] = useState<CompteComptable>({
      code: '',
      libelle: '',
      type: 'actif',
      actif: true,
    }),
    [edition, setEdition] = useState(false)
  const [occupe, setOccupe] = useState(false)
  const titre = useRef<HTMLHeadingElement>(null),
    premiereVue = useRef(true)
  useEffect(() => {
    if (premiereVue.current) {
      premiereVue.current = false
      return
    }
    setErreur('')
    setAvis('')
    titre.current?.focus()
    titre.current?.scrollIntoView({ block: 'start' })
  }, [vue])
  const api = async (chemin: string, options: RequestInit = {}) => {
    const rep = await fetch(`${baseUrl}/api/admin/finances${chemin}`, {
      ...options,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', ...options.headers },
    })
    if (rep.status === 401) onAuth?.()
    if (!rep.ok) {
      const e = await rep.json().catch(() => ({}))
      throw new Error(
        e.erreur?.message ??
          e.message ??
          'Cette opération n’a pas pu être terminée.',
      )
    }
    return rep
  }
  const filtre = () =>
    new URLSearchParams({ du, au: suivant(fin), environnement: env }).toString()
  useEffect(() => {
    if (!autorise('finances.lire') || vue === 'commerce') return
    let actif = true
    setCharge(true)
    setErreur('')
    Promise.all([
      api('/configuration').then((r) => r.json()),
      api(`/rapport?${filtre()}`).then((r) => r.json()),
      api(`/suivi?environnement=${encodeURIComponent(env)}`).then((r) => r.json()),
    ])
      .then(([c, r, s]) => {
        if (actif) {
          setConfiguration(c)
          setRapport(r)
          setSuivi(s)
        }
      })
      .catch((e) => {
        if (actif) setErreur(e.message)
      })
      .finally(() => {
        if (actif) setCharge(false)
      })
    return () => {
      actif = false
    }
  }, [baseUrl, du, fin, env, revision, permissions.join(','), vue])
  const mutation = async (chemin: string, corps: unknown, method = 'POST') => {
    setOccupe(true)
    setErreur('')
    setAvis('')
    try {
      const r = await api(chemin, {
        method,
        body: corps == null ? undefined : JSON.stringify(corps),
      })
      const data = await r.json()
      setRevision((v) => v + 1)
      setAvis(data.message ?? 'Les changements sont enregistrés.')
      return true
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible.')
      return false
    } finally {
      setOccupe(false)
    }
  }
  const exporter = async (format: 'transactions' | 'ecritures') => {
    setOccupe(true)
    setErreur('')
    try {
      const r = await api(`/export.csv?${filtre()}&format=${format}`)
      const fichier = await r.blob(),
        url = URL.createObjectURL(fichier),
        lien = document.createElement('a')
      lien.href = url
      lien.download = `funo-${format}-${du}-${fin}-${env}.csv`
      lien.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setAvis('Le fichier CSV a été téléchargé.')
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Export impossible.')
    } finally {
      setOccupe(false)
    }
  }
  if (!autorise('finances.lire'))
    return (
      <section class="finance-pane">
        <style>{stylesFinances}</style>
        <div class="finance-empty">
          <h2>Finances</h2>
          <p>Votre rôle ne donne pas accès aux données financières.</p>
        </div>
      </section>
    )
  const anomaliesMapping =
    rapport?.anomalies.filter((a) => a.code === 'mapping').length ?? 0
  return (
    <section class="finance-pane">
      <style>{stylesFinances}</style>
      <header class="finance-heading">
        <div>
          <p class="finance-eyebrow">Votre activité, en chiffres</p>
          <h2 ref={titre} tabIndex={-1}>
            {vue === 'commerce' ? 'Ventes Stripe' : 'Rapport financier'}
          </h2>
          <p>{vue === 'commerce' ? 'Suivez chaque vente, du panier au paiement confirmé.' : 'Les montants encaissés et remboursés, prêts à rapprocher.'}</p>
        </div>
        <nav class="finance-pages" aria-label="Vues financières">
          {([['commerce', 'Ventes Stripe'], ['rapport', 'Rapport'], ['suivi', 'Paiements et soldes'], ['factures', 'Factures DEMO'], ['lots', 'Lots et postes'], ['configuration', 'Comptes et correspondances']] as const).map(([cle, libelle]) => (
            <button key={cle} type="button" class="finance-button" aria-current={vue === cle ? 'page' : undefined} onClick={() => setVue(cle)}>{libelle}</button>
          ))}
        </nav>
      </header>
      {erreur && (
        <div class="finance-alert finance-alert-error" role="alert">
          {erreur}
        </div>
      )}
      {avis && (
        <div class="finance-alert" role="status">
          {avis}
        </div>
      )}
      {vue === 'commerce' ? (
        <CommercePane baseUrl={baseUrl} permissions={permissions} onAuth={onAuth} onOuvrirDemande={onOuvrirDemande} />
      ) : vue === 'rapport' ? (
        <>
          <div class="finance-toolbar">
            <label>
              Du
              <input
                type="date"
                value={du}
                max={fin}
                onChange={(e) =>
                  e.currentTarget.value && setDu(e.currentTarget.value)
                }
              />
            </label>
            <label>
              Au
              <input
                type="date"
                value={fin}
                min={du}
                onChange={(e) =>
                  e.currentTarget.value && setFin(e.currentTarget.value)
                }
              />
            </label>
            <label>
              Transactions
              <select
                aria-label="Transactions"
                value={env}
                onChange={(e) => setEnv(e.currentTarget.value)}
              >
                {Object.entries(nomsEnv).map(([v, n]) => (
                  <option value={v}>{n}</option>
                ))}
              </select>
            </label>
            <span class="finance-toolbar-note">
              {configuration?.fuseau ?? 'America/Toronto'} · CAD
            </span>
            <button
              class="finance-button"
              disabled={occupe || charge || !autorise('finances.exporter')}
              onClick={() => exporter('transactions')}
            >
              Exporter le rapport ↓
            </button>
          </div>
          {env !== 'actif' && (
            <div class="finance-mode-note">
              {nomsEnv[env]} · Ces montants sont présentés séparément des
              encaissements réels.
            </div>
          )}
          <div class="finance-kpis">
            {[
              ['Encaissements', rapport?.totaux.encaissements_cents],
              ['Remboursements', rapport?.totaux.remboursements_cents],
              ['Flux net', rapport?.totaux.net_cents],
              ['Dépôts nets', rapport?.totaux.depots_cents],
            ].map(([label, valeur]) => (
              <article>
                <span>{label}</span>
                <strong>{charge ? '—' : argent(Number(valeur ?? 0))}</strong>
              </article>
            ))}
          </div>
          {rapport &&
            (rapport.anomalies.length > 0 || rapport.sans_date.length > 0) && (
              <div class="finance-readiness">
                <div>
                  <strong>
                    {anomaliesMapping
                      ? 'Quelques correspondances restent à choisir'
                      : 'Des opérations demandent une vérification'}
                  </strong>
                  <p>
                    {anomaliesMapping
                      ? 'Associez vos comptes pour préparer les écritures comptables. Le rapport des transactions reste disponible.'
                      : 'Les informations historiques manquantes sont signalées. Aucun montant ni date ne sera inventé.'}
                  </p>
                </div>
                <button
                  class="finance-button"
                  onClick={() => setVue('configuration')}
                >
                  Voir les correspondances →
                </button>
              </div>
            )}
          <div class="finance-card">
            <div class="finance-card-head">
              <div class="finance-segments">
                <button
                  class={onglet === 'transactions' ? 'selected' : ''}
                  onClick={() => setOnglet('transactions')}
                >
                  Transactions
                </button>
                <button
                  class={onglet === 'ecritures' ? 'selected' : ''}
                  onClick={() => setOnglet('ecritures')}
                >
                  Écritures comptables
                </button>
              </div>
              {onglet === 'ecritures' && (
                <button
                  class="finance-button finance-primary"
                  disabled={
                    occupe ||
                    charge ||
                    !rapport?.export_comptable_possible ||
                    !autorise('finances.exporter')
                  }
                  onClick={() => exporter('ecritures')}
                >
                  Exporter les écritures ↓
                </button>
              )}
            </div>
            {onglet === 'ecritures' && (
              <div class="finance-ledger-summary">
                <span>
                  Débits <b>{argent(rapport?.totaux.debit_cents ?? 0)}</b>
                </span>
                <span>
                  Crédits <b>{argent(rapport?.totaux.credit_cents ?? 0)}</b>
                </span>
                <span class="finance-pill">
                  {rapport?.export_comptable_possible
                    ? 'Prêt à exporter'
                    : 'Aperçu à compléter'}
                </span>
              </div>
            )}
            {onglet === 'ecritures' && env !== 'actif' && (
              <p class="finance-inline-note">
                Cet aperçu permet de vérifier les correspondances. L’export
                comptable est réservé aux paiements réels.
              </p>
            )}
            {charge ? (
              <div class="finance-empty" aria-live="polite">
                Chargement du rapport…
              </div>
            ) : onglet === 'transactions' ? (
              !rapport?.transactions.length ? (
                <div class="finance-empty">
                  <span class="finance-empty-icon">↗</span>
                  <h3>Aucune transaction sur cette période</h3>
                  <p>
                    Choisissez une autre période ou consultez les transactions
                    de démonstration.
                  </p>
                  {env === 'actif' && (
                    <button
                      class="finance-button"
                      onClick={() => setEnv('simule')}
                    >
                      Voir les données de démonstration
                    </button>
                  )}
                </div>
              ) : (
                <div class="finance-scroll">
                  <table class="finance-transactions">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Service</th>
                        <th>Opération</th>
                        <th>Mode</th>
                        <th class="finance-number">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapport.transactions.map((t) => (
                        <tr key={t.id}>
                          <td>{t.date_locale ?? 'Date inconnue'}</td>
                          <td>
                            <strong>
                              {t.service_nom ||
                                t.service_cle ||
                                'Service historique'}
                            </strong>
                          </td>
                          <td>
                            {
                              {
                                encaissement: 'Encaissement',
                                remboursement: 'Remboursement',
                                retenue: 'Retenue sur carte',
                                liberation: 'Retenue libérée',
                              }[t.type]
                            }
                            <small>
                              {t.nature === 'depot'
                                ? 'Dépôt de garantie'
                                : 'Tarif'}
                            </small>
                          </td>
                          <td>
                            <span class="finance-pill">
                              {nomsEnv[t.environnement]}
                            </span>
                          </td>
                          <td class="finance-number">
                            {t.type === 'remboursement' ? '− ' : ''}
                            {argent(t.montant_cents)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <>
                <div class="finance-scroll">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Compte</th>
                        <th>Libellé</th>
                        <th class="finance-number">Débit</th>
                        <th class="finance-number">Crédit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rapport?.ecritures.map((l, i) => (
                        <tr key={`${l.mouvement_id}:${i}`}>
                          <td>{l.date_locale}</td>
                          <td>
                            <strong>{l.compte_code}</strong>
                            <small>{l.compte_libelle}</small>
                          </td>
                          <td>{l.libelle}</td>
                          <td class="finance-number">
                            {l.debit_cents ? argent(l.debit_cents) : '—'}
                          </td>
                          <td class="finance-number">
                            {l.credit_cents ? argent(l.credit_cents) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!rapport?.ecritures.length && (
                  <div class="finance-empty">
                    <h3>Vos écritures apparaîtront ici</h3>
                    <p>
                      Ajoutez votre charte de comptes, puis reliez les éléments
                      payants à leurs comptes.
                    </p>
                    <button
                      class="finance-button"
                      onClick={() => setVue('configuration')}
                    >
                      Configurer les correspondances
                    </button>
                  </div>
                )}
                {!!rapport?.anomalies.length && (
                  <ul class="finance-anomalies">
                    {rapport.anomalies.slice(0, 12).map((a, i) => (
                      <li key={i}>{a.message}</li>
                    ))}
                    {rapport.anomalies.length > 12 && (
                      <li>
                        Et {rapport.anomalies.length - 12} autres opérations à
                        vérifier.
                      </li>
                    )}
                  </ul>
                )}
              </>
            )}
          </div>
          <p class="finance-footnote">
            Rapport de mouvements en CAD. Les dépôts nets correspondent aux
            dépôts encaissés moins les dépôts remboursés sur la période ; les
            retenues non encaissées ne produisent pas d’écriture comptable.{' '}
            {rapport?.sans_date.length
              ? `${rapport.sans_date.length} opération(s) sans date vérifiable sont disponibles dans le CSV brut, hors totaux de période.`
              : ''}
          </p>
        </>
      ) : vue === 'suivi' ? (
        <>
          <div class="finance-toolbar">
            <label>
              Environnement
              <select value={env} onChange={(e) => setEnv(e.currentTarget.value)}>
                {Object.entries(nomsEnv).map(([valeur, nom]) => <option value={valeur}>{nom}</option>)}
              </select>
            </label>
            <p class="finance-footnote">État courant, indépendant des dates du rapport.</p>
          </div>
          <div class="finance-card finance-suivi-note">
            <div class="finance-card-head"><h3>Factures et reçus émis par Funo</h3></div>
            <p>{suivi?.documents_emis.length ?? 0} document émis. Funo ne génère pas encore de facture fiscale ni de reçu PDF de paiement. Le contrat signé et la trace de paiement sont distincts d’une facture.</p>
          </div>
          <div class="finance-kpis finance-suivi-kpis">
            <article><span>Montants à encaisser dans les parcours</span><strong>{argent(suivi?.total_a_encaisser_cents ?? 0)}</strong></article>
            <article><span>Instructions de paiement ouvertes</span><strong>{suivi?.montants_a_encaisser.length ?? 0}</strong></article>
          </div>
          <div class="finance-card">
            <div class="finance-card-head"><h3>Montants à encaisser</h3></div>
            {!suivi?.montants_a_encaisser.length ? <div class="finance-empty">Aucun paiement tarifaire ouvert dans cet environnement.</div> : (
              <div class="finance-scroll"><table><thead><tr><th>Demande</th><th>Service</th><th>Statut</th><th class="finance-number">À régler</th></tr></thead><tbody>
                {suivi.montants_a_encaisser.map((ligne) => <tr key={ligne.paiement_id}>
                  <td>{ligne.demandeur || ligne.organisation || ligne.courriel || 'Demandeur non renseigné'}{onOuvrirDemande ? <button class="finance-link" type="button" onClick={() => onOuvrirDemande(ligne.demande_id)}>Voir la demande</button> : null}</td>
                  <td>{ligne.service_cle}</td><td>{ligne.etat_paiement === 'echoue' ? 'Échec de paiement' : 'En attente de paiement'}</td><td class="finance-number">{argent(ligne.montant_cents)}</td>
                </tr>)}
              </tbody></table></div>
            )}
          </div>
          <div class="finance-card">
            <div class="finance-card-head"><h3>Paiements enregistrés</h3></div>
            {!suivi?.paiements.length ? <div class="finance-empty">Aucun paiement enregistré dans cet environnement.</div> : (
              <div class="finance-scroll"><table><thead><tr><th>Créé le</th><th>Demande</th><th>Nature</th><th>État</th><th class="finance-number">Montant</th></tr></thead><tbody>
                {suivi.paiements.map((ligne) => <tr key={ligne.paiement_id}>
                  <td>{ligne.cree_le_utc.slice(0, 10)}</td><td>{ligne.demandeur || ligne.organisation || ligne.courriel || ligne.demande_id}{onOuvrirDemande ? <button class="finance-link" type="button" onClick={() => onOuvrirDemande(ligne.demande_id)}>Voir</button> : null}</td>
                  <td>{ligne.nature === 'depot' ? 'Dépôt' : 'Tarif'}</td><td>{({a_payer:'À payer',paye:'Payé',echoue:'Échec',rembourse:'Remboursé',libere:'Libéré'} as Record<string,string>)[ligne.etat_paiement] ?? ligne.etat_paiement}</td>
                  <td class="finance-number">{argent(ligne.montant_cents)}{ligne.rembourse_cents ? <small>dont {argent(ligne.rembourse_cents)} remboursés</small> : null}</td>
                </tr>)}
              </tbody></table></div>
            )}
          </div>
          {suivi?.tronque ? <p class="finance-footnote">Liste limitée aux 500 paiements les plus récents : les totaux affichés ne couvrent que ces lignes.</p> : null}
          <p class="finance-footnote">{suivi?.limites.join(' ')}</p>
        </>
      ) : vue === 'factures' || vue === 'lots' ? (
        <FinancesDocumentsPane baseUrl={baseUrl} vue={vue} du={du} au={suivant(fin)} environnement={env} permissions={permissions} onAuth={onAuth} onOuvrirDemande={onOuvrirDemande} />
      ) : (
        <>
          <div class="finance-settings-intro">
            <h3>Votre charte, vos comptes</h3>
            <p>
              Importez les comptes de votre organisation ou ajoutez-les ici.
              Aucun numéro de compte n’est proposé automatiquement.
            </p>
          </div>
          <div class="finance-card">
            <div class="finance-card-head">
              <h3>1. Charte de comptes</h3>
              {autorise('finances.modifier') && (
                <label
                  class="finance-button finance-upload"
                  role="button"
                  tabIndex={0}
                  aria-label="Importer une charte de comptes CSV"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.currentTarget.querySelector('input')?.click()
                    }
                  }}
                >
                  Importer un CSV
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    disabled={occupe}
                    onChange={async (e) => {
                      const input = e.currentTarget
                      const fichier = input.files?.[0]
                      if (fichier) {
                        await mutation('/comptes/importer', {
                          csv: await fichier.text(),
                        })
                        input.value = ''
                      }
                    }}
                  />
                </label>
              )}
            </div>
            <p class="finance-inline-note">
              Colonnes : code, libelle, type, actif. Types : actif, passif,
              produit, charge, capitaux. Séparateur virgule ou point-virgule.
            </p>
            {autorise('finances.modifier') && (
              <form
                class="finance-account-form"
                onSubmit={async (e) => {
                  e.preventDefault()
                  if (await mutation('/comptes', compte)) {
                    setCompte({
                      code: '',
                      libelle: '',
                      type: 'actif',
                      actif: true,
                    })
                    setEdition(false)
                  }
                }}
              >
                <label>
                  Code
                  <input
                    required
                    maxLength={40}
                    value={compte.code}
                    disabled={edition}
                    placeholder="Votre code"
                    onInput={(e) =>
                      setCompte({ ...compte, code: e.currentTarget.value })
                    }
                  />
                </label>
                <label>
                  Libellé
                  <input
                    required
                    maxLength={160}
                    value={compte.libelle}
                    placeholder="Nom du compte"
                    onInput={(e) =>
                      setCompte({ ...compte, libelle: e.currentTarget.value })
                    }
                  />
                </label>
                <label>
                  Type
                  <select
                    value={compte.type}
                    onChange={(e) =>
                      setCompte({
                        ...compte,
                        type: e.currentTarget.value as TypeCompte,
                      })
                    }
                  >
                    {Object.entries(types).map(([v, n]) => (
                      <option value={v}>{n}</option>
                    ))}
                  </select>
                </label>
                <button
                  class="finance-button finance-primary"
                  disabled={occupe}
                >
                  {edition ? 'Enregistrer' : 'Ajouter le compte'}
                </button>
                {edition && (
                  <button
                    type="button"
                    class="finance-button"
                    onClick={() => {
                      setEdition(false)
                      setCompte({
                        code: '',
                        libelle: '',
                        type: 'actif',
                        actif: true,
                      })
                    }}
                  >
                    Annuler
                  </button>
                )}
              </form>
            )}
            {configuration?.comptes.length ? (
              <div class="finance-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Libellé</th>
                      <th>Type</th>
                      <th>État</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {configuration.comptes.map((c) => (
                      <tr key={c.code}>
                        <td>
                          <strong>{c.code}</strong>
                        </td>
                        <td>{c.libelle}</td>
                        <td>{types[c.type]}</td>
                        <td>{c.actif ? 'Actif' : 'Inactif'}</td>
                        <td class="finance-number">
                          {autorise('finances.modifier') && (
                            <>
                              <button
                                class="finance-link"
                                onClick={() => {
                                  setCompte(c)
                                  setEdition(true)
                                }}
                              >
                                Modifier
                              </button>
                              <button
                                class="finance-link"
                                disabled={occupe}
                                onClick={() =>
                                  mutation('/comptes', {
                                    ...c,
                                    actif: !c.actif,
                                  })
                                }
                              >
                                {c.actif ? 'Désactiver' : 'Activer'}
                              </button>
                              {!c.actif && (
                                <button
                                  class="finance-link"
                                  disabled={occupe}
                                  onClick={() =>
                                    mutation(
                                      `/comptes/${encodeURIComponent(c.code)}`,
                                      null,
                                      'DELETE',
                                    )
                                  }
                                >
                                  Supprimer
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div class="finance-empty">
                <h3>Ajoutez votre premier compte</h3>
                <p>
                  Vous pourrez ensuite choisir où enregistrer les encaissements,
                  les produits, les dépôts et les taxes.
                </p>
              </div>
            )}
          </div>
          <div class="finance-card">
            <div class="finance-card-head">
              <h3>2. Correspondances des éléments payants</h3>
              <span class="finance-pill">
                {configuration?.elements.length ?? 0} éléments
              </span>
            </div>
            <p class="finance-inline-note">
              Chaque tarif et dépôt utilise vos comptes. Les écritures déjà
              enregistrées gardent leurs correspondances d’origine.
            </p>
            {configuration?.elements.length ? (
              configuration.elements.map((element) =>
                element.compte_gl ? (
                  <details
                    class="finance-mapping"
                    key={`${element.service_cle}:${element.regle_cle}`}
                  >
                    <summary>
                      <div>
                        <strong>{element.libelle}</strong>
                        <span>
                          {element.service_nom} ·{' '}
                          {element.compte_gl.compte_credit}
                        </span>
                      </div>
                      <span class="finance-pill">
                        {element.item_id ? 'GL de l’article' : 'GL du parcours'}
                      </span>
                    </summary>
                    <div class="finance-mapping-fields">
                      <div>
                        <small>Encaissement · débit</small>
                        <strong>{element.compte_gl.compte_debit}</strong>
                        <small>
                          {
                            configuration.comptes.find(
                              (c) => c.code === element.compte_gl!.compte_debit,
                            )?.libelle
                          }
                        </small>
                      </div>
                      <div>
                        <small>
                          {element.compte_gl.traitement === 'differe'
                            ? 'Produit différé'
                            : 'Produit'}{' '}
                          · crédit
                        </small>
                        <strong>{element.compte_gl.compte_credit}</strong>
                        <small>
                          {
                            configuration.comptes.find(
                              (c) =>
                                c.code === element.compte_gl!.compte_credit,
                            )?.libelle
                          }
                        </small>
                      </div>
                      <div>
                        <small>Taxes</small>
                        {Object.entries(element.compte_gl.taxes).map(
                          ([nom, code]) => (
                            <span style={{ display: 'block' }}>
                              {nom} · {code}
                            </span>
                          ),
                        )}
                      </div>
                    </div>
                    <p class="finance-inline-note">
                      Compte conservé dans chaque ligne facturée et ses
                      remboursements.{' '}
                      <a href={element.item_id ? '#inventaire' : '#evenements'}>
                        Modifier{' '}
                        {element.item_id
                          ? 'l’article dans l’inventaire'
                          : 'le parcours'}{' '}
                        →
                      </a>
                    </p>
                  </details>
                ) : (
                  <MappingEditor
                    key={`${element.service_cle}:${element.regle_cle}`}
                    element={element}
                    comptes={configuration.comptes}
                    actuel={configuration.mappings.find(
                      (m) =>
                        m.service_cle === element.service_cle &&
                        m.regle_cle === element.regle_cle &&
                        m.nature === element.nature,
                    )}
                    modifiable={autorise('finances.modifier')}
                    occupe={occupe}
                    enregistrer={(m) => mutation('/mappings', m)}
                  />
                ),
              )
            ) : (
              <div class="finance-empty">
                Les tarifs et dépôts de vos services apparaîtront ici dès qu’ils
                seront configurés.
              </div>
            )}
          </div>
          <div class="finance-settings-end">
            <p>
              Le CSV universel fournit les dates, comptes, libellés, débits et
              crédits. Choisissez le format d’import approprié dans votre
              logiciel comptable.
            </p>
            <button
              class="finance-button finance-primary"
              onClick={() => {
                setVue('rapport')
                setOnglet('ecritures')
              }}
            >
              3. Vérifier les écritures →
            </button>
          </div>
        </>
      )}
    </section>
  )
}
function MappingEditor({
  element,
  comptes,
  actuel,
  modifiable,
  occupe,
  enregistrer,
}: {
  element: ElementPayant
  comptes: CompteComptable[]
  actuel?: MappingComptable
  modifiable: boolean
  occupe: boolean
  enregistrer: (m: MappingComptable) => Promise<boolean>
}) {
  const [m, setM] = useState<MappingComptable>(
    actuel ?? {
      service_cle: element.service_cle,
      regle_cle: element.regle_cle,
      nature: element.nature,
      compte_debit: '',
      compte_credit: '',
      traitement: element.nature === 'depot' ? 'depot' : 'produit',
      taxes: {},
    },
  )
  useEffect(() => {
    if (actuel) setM(actuel)
  }, [actuel])
  const choix = (type: string, selection: string) => (
    <>
      <option value="">Choisir un compte</option>
      {comptes
        .filter((c) => (c.actif && c.type === type) || c.code === selection)
        .map((c) => (
          <option value={c.code}>
            {c.code} · {c.libelle}
            {!c.actif ? ' (inactif)' : ''}
          </option>
        ))}
    </>
  )
  return (
    <details class="finance-mapping">
      <summary>
        <div>
          <strong>{element.libelle}</strong>
          <span>
            {element.service_nom} ·{' '}
            {element.nature === 'depot' ? 'Dépôt' : 'Tarif'}
          </span>
        </div>
        <span class={`finance-pill ${actuel ? '' : 'finance-pill-warning'}`}>
          {actuel ? 'Associé' : 'À configurer'}
        </span>
      </summary>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          await enregistrer(m)
        }}
      >
        <div class="finance-mapping-fields">
          <label>
            Encaissement · débit
            <select
              required
              disabled={!modifiable}
              value={m.compte_debit}
              onChange={(e) =>
                setM({ ...m, compte_debit: e.currentTarget.value })
              }
            >
              {choix('actif', m.compte_debit)}
            </select>
          </label>
          {element.nature === 'tarif' && (
            <label>
              Traitement du tarif
              <select
                disabled={!modifiable}
                value={m.traitement}
                onChange={(e) =>
                  setM({
                    ...m,
                    traitement: e.currentTarget
                      .value as MappingComptable['traitement'],
                    compte_credit: '',
                  })
                }
              >
                <option value="produit">Produit</option>
                <option value="differe">Produit différé</option>
              </select>
            </label>
          )}
          <label>
            {element.nature === 'depot'
              ? 'Dépôt à rendre · crédit'
              : m.traitement === 'differe'
                ? 'Produit différé · crédit'
                : 'Produit · crédit'}
            <select
              required
              disabled={!modifiable}
              value={m.compte_credit}
              onChange={(e) =>
                setM({ ...m, compte_credit: e.currentTarget.value })
              }
            >
              {choix(
                m.traitement === 'produit' ? 'produit' : 'passif',
                m.compte_credit,
              )}
            </select>
          </label>
          {element.taxes.map((n) => (
            <label>
              {n} · crédit
              <select
                required
                disabled={!modifiable}
                value={m.taxes[n] ?? ''}
                onChange={(e) =>
                  setM({
                    ...m,
                    taxes: { ...m.taxes, [n]: e.currentTarget.value },
                  })
                }
              >
                {choix('passif', m.taxes[n] ?? '')}
              </select>
            </label>
          ))}
        </div>
        {modifiable && (
          <button class="finance-button finance-primary" disabled={occupe}>
            Enregistrer la correspondance
          </button>
        )}
      </form>
    </details>
  )
}
export const stylesFinances = `
.finance-pages{display:flex;gap:7px;flex-wrap:wrap}.finance-pages .finance-button[aria-current=page]{background:#f6f0f9;border-color:#bcbdec;color:#391d44}.finance-suivi-note{padding:0 22px 18px}.finance-suivi-note .finance-card-head{padding-left:0}.finance-suivi-note p{margin:0;color:#6e5c76;font-size:13px;line-height:1.6}.finance-suivi-kpis{grid-template-columns:repeat(2,minmax(0,1fr))}.finance-suivi-kpis strong{font-size:22px}@media(max-width:540px){.finance-pages{width:100%}.finance-pages .finance-button{flex:1;white-space:normal}.finance-suivi-kpis{grid-template-columns:1fr}}
.finance-pane{--f-ink:#2a1632;--f-muted:#6e5c76;--f-border:#ece2f0;--f-blue:#391d44;color:var(--f-ink);font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;max-width:1240px;margin:auto;padding:8px 0 32px}.finance-pane *{box-sizing:border-box}.finance-heading{display:flex;align-items:center;justify-content:space-between;gap:24px;margin:8px 0 30px}.finance-heading h2{font-size:22px;letter-spacing:-.5px;margin:0 0 6px;font-weight:650}.finance-heading p{color:var(--f-muted);margin:0}.finance-heading .finance-eyebrow{display:none;font-size:11px;text-transform:uppercase;letter-spacing:1.6px;margin-bottom:8px}.finance-pane button,.finance-upload{font:inherit;cursor:pointer;white-space:nowrap}.finance-button{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-height:38px;border:1px solid var(--f-border);border-radius:10px;padding:8px 14px;background:white;color:var(--f-ink);font-weight:550}.finance-primary{color:white;background:var(--f-blue);border-color:var(--f-blue)}.finance-button:disabled{opacity:.42;cursor:default}.finance-pane input,.finance-pane select{font:inherit;min-height:40px;border:1px solid #dbd3df;border-radius:9px;background:white;padding:8px 10px;color:var(--f-ink);min-width:0;width:100%}.finance-pane label{display:flex;flex-direction:column;gap:6px;font-size:12px;color:var(--f-muted)}.finance-toolbar{display:flex;gap:14px;align-items:end;margin-bottom:22px;flex-wrap:wrap}.finance-toolbar label{min-width:138px}.finance-toolbar-note{align-self:center;color:var(--f-muted);font-size:12px;flex:1;text-align:right;padding-top:20px}.finance-kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin:18px 0 24px}.finance-kpis article{background:#fff;border:1px solid var(--f-border);border-radius:16px;padding:22px}.finance-kpis span{color:var(--f-muted);font-size:12px;display:block}.finance-kpis strong{display:block;font-size:28px;letter-spacing:-.8px;margin-top:8px;font-weight:600;font-variant-numeric:tabular-nums}.finance-card{background:#fff;border:1px solid var(--f-border);border-radius:18px;margin-top:20px;overflow:hidden}.finance-card-head{padding:20px 22px;display:flex;align-items:center;justify-content:space-between;gap:14px}.finance-card h3{font-size:16px;margin:0;font-weight:600}.finance-segments{display:flex;background:#f6f1f8;border-radius:9px;padding:3px;gap:2px}.finance-segments button{border:0;background:transparent;padding:7px 13px;color:var(--f-muted);border-radius:7px;font-size:12px}.finance-segments button.selected{background:white;color:var(--f-ink);box-shadow:0 1px 4px #00000012}.finance-scroll{overflow:auto}.finance-pane td:nth-child(2){white-space:normal;min-width:140px;max-width:230px}.finance-heading h2{scroll-margin-top:85px}.finance-heading h2:focus{outline:none}.finance-pane table{border-collapse:collapse;width:100%;white-space:nowrap}.finance-pane th{font-size:11px;font-weight:550;letter-spacing:.4px;color:var(--f-muted);text-align:left;background:#fbf9fc}.finance-pane th,.finance-pane td{padding:14px 22px;border-top:1px solid #f3ecf5}.finance-pane td{font-size:13px}.finance-pane td strong{font-weight:550}.finance-pane small{display:block;font-size:11px;color:var(--f-muted);margin-top:3px}.finance-pane .finance-number{text-align:right;font-variant-numeric:tabular-nums}.finance-empty{text-align:center;padding:52px 24px;color:var(--f-muted)}.finance-empty h3{margin:10px 0 8px;font-size:17px;color:var(--f-ink)}.finance-empty p{max-width:450px;margin:0 auto 18px}.finance-empty-icon{font-size:27px;display:inline-grid;place-items:center;background:#f7f3f9;border-radius:16px;width:56px;height:56px;color:#6b5a70}.finance-readiness{border:1px solid #eadfca;background:#fffaf0;border-radius:14px;padding:18px 22px;display:flex;align-items:center;gap:24px;justify-content:space-between}.finance-readiness p{margin:4px 0 0;color:#76637e;font-size:12px}.finance-readiness strong{font-size:13px;font-weight:600}.finance-alert{padding:12px 16px;border-radius:10px;background:#f7f2f8;color:#275532;margin-bottom:18px}.finance-alert-error{background:#fff0ee;color:#a33323}.finance-mode-note{font-size:12px;padding:10px 14px;background:#f7f2fa;border-radius:9px;color:#391d44}.finance-pill{border-radius:6px;padding:4px 7px;display:inline-block;font-size:10px;background:#f6f1f8;color:#7b6783;white-space:nowrap}.finance-pill-warning{background:#fff3dc;color:#886625}.finance-ledger-summary{display:flex;align-items:center;gap:24px;padding:0 22px 18px;color:var(--f-muted);font-size:12px}.finance-ledger-summary b{display:inline-block;margin-left:7px;color:var(--f-ink)}.finance-footnote{font-size:11px;color:var(--f-muted);margin:18px 2px;line-height:1.6}.finance-settings-intro h3{font-size:21px;margin:0 0 6px}.finance-settings-intro p{color:var(--f-muted);margin:0;max-width:650px}.finance-inline-note{font-size:12px;color:var(--f-muted);margin:0;padding:0 22px 18px}.finance-upload input{display:none}.finance-account-form{display:flex;align-items:end;gap:12px;padding:0 22px 22px;flex-wrap:wrap}.finance-account-form label:nth-child(1){width:130px}.finance-account-form label:nth-child(2){flex:1;min-width:180px}.finance-link{background:none;border:0;color:var(--f-blue);font-size:12px!important;margin-left:12px;padding:0}.finance-mapping{border-top:1px solid var(--f-border);padding:0 22px}.finance-mapping summary{display:flex;align-items:center;justify-content:space-between;cursor:pointer;padding:18px 0;gap:12px}.finance-mapping summary strong{font-weight:550}.finance-mapping summary span:not(.finance-pill){display:block;font-size:12px;color:var(--f-muted);margin-top:3px}.finance-mapping-fields{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;padding-bottom:18px}.finance-mapping form{padding-bottom:20px}.finance-settings-end{display:flex;justify-content:space-between;align-items:center;gap:24px;margin-top:24px}.finance-settings-end p{color:var(--f-muted);font-size:12px;max-width:630px}.finance-anomalies{padding:10px 40px 20px;color:#946828;font-size:12px}.finance-pane :focus-visible{outline:3px solid #391d4440;outline-offset:2px}@media(max-width:1100px){.finance-pane th,.finance-pane td{padding:13px 12px}.finance-pane td:nth-child(2){max-width:190px}}@media(max-width:850px){.finance-kpis{grid-template-columns:repeat(2,1fr)}.finance-mapping-fields{grid-template-columns:1fr 1fr}.finance-heading h2{font-size:22px}.finance-heading{align-items:flex-start}.finance-toolbar-note{display:none}.finance-readiness{align-items:flex-start;flex-direction:column;gap:12px}}@media(max-width:540px){.finance-transactions,.finance-transactions tbody{display:block}.finance-transactions thead{display:none}.finance-transactions tr{display:grid;grid-template-columns:minmax(0,1fr) auto;grid-template-areas:"service amount" "operation amount" "date mode";gap:7px 12px;padding:16px;border-top:1px solid var(--f-border)}.finance-pane .finance-transactions td{display:block;border:0;padding:0;white-space:normal;min-width:0;max-width:none}.finance-transactions td:nth-child(1){grid-area:date;font-size:11px;color:var(--f-muted)}.finance-transactions td:nth-child(2){grid-area:service}.finance-transactions td:nth-child(3){grid-area:operation;font-size:12px}.finance-transactions td:nth-child(4){grid-area:mode;text-align:right}.finance-transactions td:nth-child(5){grid-area:amount;font-size:16px;font-weight:600}.finance-heading{flex-direction:column;gap:15px}.finance-kpis{gap:10px}.finance-kpis article{padding:16px}.finance-kpis strong{font-size:22px}.finance-card-head,.finance-ledger-summary{flex-wrap:wrap;padding-left:16px;padding-right:16px}.finance-mapping-fields{grid-template-columns:1fr}.finance-settings-end{flex-direction:column;align-items:flex-start}.finance-toolbar label{flex:1;min-width:120px}.finance-toolbar>.finance-button{width:100%}.finance-pane th,.finance-pane td{padding:12px 16px}}
`
